/**
 * @fileoverview Hook Data Layer para carga y gestión de eventos de cliente en Inbox con soporte de paginación.
 * @module useCustomerEventsData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 2.0.0
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import customerEventsModelMC from '../../../models/CustomerEventsModelMC';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { useSidebarModalMC } from '../../shared/ui/useSidebarModalMC';
import { sharingInformationService } from '../../../../services/sharingInformationService';

/**
 * @typedef {Object} PaginationInfo
 * @description Información de paginación para eventos de cliente
 * @property {number} currentPage - Página actual (1-indexed)
 * @property {number} totalPages - Total de páginas disponibles
 * @property {number} totalItems - Total de elementos
 * @property {boolean} hasNextPage - Indica si hay página siguiente
 * @property {boolean} hasPrevPage - Indica si hay página anterior
 */

/**
 * Hook de Data Layer para gestión de eventos de cliente con paginación completa.
 * 
 * Implementa el patrón de **Data Layer** con separación estricta de responsabilidades,
 * enfocándose en carga de datos de eventos con soporte de paginación avanzada.
 * 
 * @function useCustomerEventsData
 * @category Hook - Data Layer
 * 
 * @description
 * **Arquitectura Optimizada (v2.0.0):**
 * - ✅ **Data Layer**: Solo carga de datos y gestión de estados de API
 * - ✅ **Pagination**: Sistema completo de paginación con navegación
 * - ✅ **Performance**: useCallback, useMemo, y optimizaciones específicas
 * - ✅ **Error Handling**: Manejo robusto con Redux actions
 * - ✅ **Real-time**: Suscripción a actualizaciones vía sharingInformationService
 * 
 * **Responsabilidades:**
 * - Carga de eventos de cliente desde la API
 * - Gestión de estados de paginación (página actual, total, navegación)
 * - Manejo de estados de loading y error
 * - Suscripción a actualizaciones en tiempo real
 * - Integración con modal sidebar para agregar actividades
 * 
 * **NO Maneja:**
 * - ❌ Estados de UI (modales, formularios, filtros)
 * - ❌ Validaciones de formularios
 * - ❌ Operaciones de escritura (crear, actualizar, eliminar eventos)
 * 
 * **Flujo de Datos:**
 * 1. **Inicialización**: Configura paginación con página inicial
 * 2. **Carga de Datos**: Obtiene eventos con información de paginación
 * 3. **Navegación**: Maneja cambios de página con recarga automática
 * 4. **Actualización**: Escucha cambios en tiempo real y recarga cuando es necesario
 * 5. **Integración**: Proporciona función para abrir modal de actividades
 * 
 * @param {Object} customer - Objeto de cliente con información básica
 * @param {string} customer.web_id - ID web del cliente para consultas API
 * @param {number} [initialPage=1] - Página inicial para la paginación
 * 
 * @returns {Object} Objeto con estados de datos y funciones de navegación
 * @returns {Array} returns.events - Lista de eventos de la página actual
 * @returns {boolean} returns.loading - Estado de carga de datos
 * @returns {PaginationInfo} returns.pagination - Información completa de paginación
 * @returns {Function} returns.loadEvents - Función para recargar eventos de la página actual
 * @returns {Function} returns.loadMoreEvents - Función para cargar más eventos al final de la lista
 * @returns {Function} returns.goToPage - Función para navegar a una página específica
 * @returns {Function} returns.nextPage - Función para ir a la siguiente página
 * @returns {Function} returns.prevPage - Función para ir a la página anterior
 * @returns {Function} returns.resetPagination - Función para resetear a la primera página
 * @returns {Function} returns.handleAddActivity - Función para abrir modal de nueva actividad
 * 
 * @see {@link CustomerEventsModelMC} Para operaciones de API de eventos
 * @see {@link useSidebarModalMC} Para manejo del modal sidebar
 * 
 * @example
 * ```javascript
 * // Uso básico con paginación
 * const { 
 *   events, 
 *   loading, 
 *   pagination, 
 *   goToPage, 
 *   nextPage, 
 *   prevPage,
 *   loadMoreEvents 
 * } = useCustomerEventsData(customer, 1);
 * 
 * // Navegar entre páginas (reemplaza datos)
 * const handlePageChange = (page) => {
 *   goToPage(page);
 * };
 * 
 * // Cargar más datos al final (infinite scroll)
 * const handleLoadMore = () => {
 *   if (pagination.hasNextPage) {
 *     loadMoreEvents();
 *   }
 * };
 * 
 * // Verificar si hay más páginas
 * if (pagination.hasNextPage) {
 *   nextPage();
 * }
 * ```
 * 
 * @example
 * ```javascript
 * // Uso con información de paginación en UI
 * const { events, loading, pagination, goToPage, loadMoreEvents } = useCustomerEventsData(customer);
 * 
 * return (
 *   <div>
 *     {loading ? <Loading /> : <EventsList events={events} />}
 *     
 *     {/* Paginación tradicional *}
 *     <Pagination
 *       currentPage={pagination.currentPage}
 *       totalPages={pagination.totalPages}
 *       onPageChange={goToPage}
 *       hasNext={pagination.hasNextPage}
 *       hasPrev={pagination.hasPrevPage}
 *     />
 *     
 *     {/* O botón "Cargar más" para infinite scroll *}
 *     {pagination.hasNextPage && (
 *       <button onClick={loadMoreEvents} disabled={loading}>
 *         {loading ? 'Cargando...' : 'Cargar más eventos'}
 *       </button>
 *     )}
 *   </div>
 * );
 * ```
 * 
 * @since 1.0.0
 * @version 2.0.0 - Agregado soporte completo de paginación con navegación
 */
export default function useCustomerEventsData(customer, initialPage = 1, chatId) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pagination, setPagination] = useState({
    currentPage: initialPage,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  const { openModal } = useSidebarModalMC({ refData: { customer } });

  /**
   * Carga eventos de cliente con información de paginación.
   * 
   * Realiza la carga de eventos desde la API y actualiza tanto los eventos
   * como la información de paginación basada en la respuesta del servidor.
   * 
   * @async
   * @function loadEvents
   * @memberof useCustomerEventsData
   * @private
   * 
   * @description
   * **Flujo de Operación:**
   * 1. **Activación Loading**: Establece estado de carga activo
   * 2. **API Request**: Solicita eventos con parámetros de paginación
   * 3. **Procesamiento**: Procesa respuesta y extrae datos de paginación
   * 4. **Actualización Estado**: Actualiza eventos y información de paginación
   * 5. **Error Handling**: Maneja errores con Redux action
   * 
   * **Estructura de Respuesta Esperada:**
   * ```javascript
   * {
   *   events: Array,
   *   total_pages: number
   * }
   * ```
   * 
   * @throws {Error} Cuando falla la comunicación con la API o datos inválidos
   * 
   * @returns {Promise<void>} Promise que se resuelve cuando la carga completa
   * 
   * @example
   * ```javascript
   * // Recarga eventos de la página actual
   * await loadEvents();
   * ```
   */
  const loadEvents = useCallback(async () => {
    if (!customer?.web_id) {
      setEvents([]);
      setPagination(prev => ({ ...prev, totalPages: 1, totalItems: 0 }));
      return;
    }

    setLoading(true);
    try {
      const params = { id: customer.web_id, page: currentPage };
      if (chatId) {
        params.chat_id = chatId;
      }
      const eventsData = await customerEventsModelMC.getCustomerEvents(params);
      
      // Si es la primera página, reemplaza. Si no, acumula al final
      if (currentPage === 1) {
        setEvents(eventsData.events || []);
      } else {
        setEvents(prevEvents => [...prevEvents, ...(eventsData.events || [])]);
      }
      
      // Procesar información de paginación directamente del response
      const totalPages = eventsData.total_pages || 1;
      const totalItems = eventsData.total_count || eventsData.events?.length || 0;
      
      const newPagination = {
        currentPage: currentPage,
        totalPages: totalPages,
        totalItems: totalItems,
        hasNextPage: currentPage < totalPages,
        hasPrevPage: currentPage > 1
      };
      
      setPagination(newPagination);
    } catch (error) {
      dispatch({
        type: LOAD_DATA_FAILURE,
        error: error.message || 'Error loading customer events.'
      });
      
      // Resetear en caso de error
      setEvents([]);
      setPagination(prev => ({ 
        ...prev, 
        totalPages: 1, 
        totalItems: 0, 
        hasNextPage: false, 
        hasPrevPage: false 
      }));
    } finally {
      setLoading(false);
    }
  }, [customer?.web_id, currentPage, chatId, dispatch]);

  /**
   * Carga más eventos y los agrega al final de la lista existente.
   */
  const loadMoreEvents = useCallback(() => {
    if (!pagination.hasNextPage || loading) return;
    setCurrentPage(prev => prev + 1);
  }, [pagination.hasNextPage, loading]);

  /**
   * Navega a una página específica.
   * 
   * Cambia la página actual y recarga los eventos correspondientes,
   * validando que la página esté dentro del rango válido.
   * 
   * @function goToPage
   * @memberof useCustomerEventsData
   * 
   * @description
   * **Validaciones:**
   * - Página debe ser mayor a 0
   * - Página debe ser menor o igual al total de páginas
   * - No navegar si ya está en la página solicitada
   * 
   * @param {number} page - Número de página a la cual navegar (1-indexed)
   * 
   * @example
   * ```javascript
   * // Navegar a la página 3
   * goToPage(3);
   * ```
   */
  const goToPage = useCallback((page) => {
    if (page < 1 || page > pagination.totalPages || page === currentPage) {
      return;
    }
    setCurrentPage(page);
  }, [pagination.totalPages, currentPage]);

  /**
   * Navega a la página siguiente.
   * 
   * Incrementa la página actual en 1 si hay páginas siguientes disponibles.
   * 
   * @function nextPage
   * @memberof useCustomerEventsData
   * 
   * @example
   * ```javascript
   * // Ir a página siguiente si está disponible
   * if (pagination.hasNextPage) {
   *   nextPage();
   * }
   * ```
   */
  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [pagination.hasNextPage]);

  /**
   * Navega a la página anterior.
   * 
   * Decrementa la página actual en 1 si hay páginas anteriores disponibles.
   * 
   * @function prevPage
   * @memberof useCustomerEventsData
   * 
   * @example
   * ```javascript
   * // Ir a página anterior si está disponible
   * if (pagination.hasPrevPage) {
   *   prevPage();
   * }
   * ```
   */
  const prevPage = useCallback(() => {
    if (pagination.hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  }, [pagination.hasPrevPage]);

  /**
   * Resetea la paginación a la primera página.
   * 
   * Útil para filtros o búsquedas que requieren volver al inicio.
   * 
   * @function resetPagination
   * @memberof useCustomerEventsData
   * 
   * @example
   * ```javascript
   * // Volver al inicio después de aplicar filtros
   * resetPagination();
   * ```
   */
  const resetPagination = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Effect para cargar eventos cuando cambia el customer o la página
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Effect para suscripción a actualizaciones en tiempo real
  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();

    const myObservable = subscription$.subscribe((data) => {
      if (data.reloadCustomerEvents) {
        resetPagination();
        loadEvents();
      }
    });

    // Cleanup: desuscribirse al desmontar componente
    return () => {
      myObservable.unsubscribe();
    };
  }, [loadEvents, resetPagination]);

  /**
   * Maneja la apertura del modal para agregar nueva actividad.
   * 
   * @function handleAddActivity
   * @memberof useCustomerEventsData
   * 
   * @example
   * ```javascript
   * // Abrir modal para nueva actividad
   * handleAddActivity();
   * ```
   */
  const handleAddActivity = useCallback(() => {
    openModal({ chatId }, 'CustomerEventSidebar');
  }, [openModal, chatId]);

  // Memoized return object para prevenir re-renders innecesarios
  return useMemo(() => ({
    events,
    loading,
    pagination,
    loadEvents,
    loadMoreEvents,
    goToPage,
    nextPage,
    prevPage,
    resetPagination,
    handleAddActivity,
  }), [
    events,
    loading, 
    pagination,
    loadEvents,
    loadMoreEvents,
    goToPage, 
    nextPage, 
    prevPage, 
    resetPagination,
    handleAddActivity
  ]);
}
